import React from 'react';
import * as Icons from 'lucide-react';
import { CheckCircle2, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { services } from '../mock/mock';
import CtaBanner from '../components/CtaBanner';
import PageMeta from '../components/PageMeta';
import { buildBreadcrumbLd } from '../lib/structuredData';

const PageHeader = ({ eyebrow, title, sub }) => (
  <section className="pt-16 pb-10 bg-[var(--rs-cream)] border-b border-[#E7E0D2]">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-4">{eyebrow}</div>
      <h1 className="h-display text-5xl md:text-6xl text-[var(--rs-ink)] max-w-4xl leading-[1.05]">{title}</h1>
      {sub && <p className="mt-5 text-[var(--rs-ink)]/70 max-w-2xl text-lg">{sub}</p>}
    </div>
  </section>
);

const Services = () => {
  return (
    <div>
      <PageMeta
        title="Services · Residential Support — Inventories, viewings, photography & floor plans"
        description="One UK partner for every property job — check-in and check-out inventories, accompanied viewings, and professional photography paired with a laser-measured floor plan."
        path="/services"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'Services', path: '/services' },
            ])
          )}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Services offered by Residential Support',
            itemListElement: services.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `https://residentialsupport.co.uk/services/${s.id}`,
              name: s.title,
            })),
          })}
        </script>
      </Helmet>
      <PageHeader
        eyebrow="Services"
        title="Every property job, handled by one trusted partner."
        sub="Pick a single service or hand us your branch's entire field workload. Pricing is fixed and every report lands in your dashboard."
      />

      <div className="bg-[var(--rs-cream)] pt-2 pb-4">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap gap-3">
          <Link to="/contact" className="btn-primary">Contact Us</Link>
        </div>
      </div>

      <section className="sect bg-[var(--rs-cream)] !pt-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-16">
          {services.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Sparkles;
            const reversed = i % 2 === 1;
            return (
              <div key={s.id} id={s.id} className={`grid lg:grid-cols-12 gap-10 items-center ${reversed ? 'lg:[direction:rtl]' : ''}`} data-testid={`services-row-${s.id}`}>
                <div className="lg:col-span-6 [direction:ltr]">
                  <Link to={`/services/${s.id}`}>
                    <img src={s.image} alt={s.title} loading="lazy" className="w-full h-[360px] lg:h-[440px] object-cover rounded-[24px] shadow-lg hover:scale-[1.01] transition-transform" />
                  </Link>
                </div>
                <div className="lg:col-span-6 [direction:ltr]">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)]">Service — {String(i + 1).padStart(2, '0')}</div>
                  </div>
                  <h2 className="h-display text-4xl md:text-5xl text-[var(--rs-ink)] mt-4">
                    <Link to={`/services/${s.id}`} className="hover:text-[var(--rs-coral)] transition-colors">{s.title}</Link>
                  </h2>
                  <p className="text-[var(--rs-ink)]/75 mt-4 text-lg">{s.description}</p>
                  <ul className="mt-6 space-y-2.5">
                    {['Reviewed before release', 'Local UK specialists', 'Digital report in your dashboard'].map((f, k) => (
                      <li key={k} className="flex items-start gap-2.5 text-[var(--rs-ink)]/80"><CheckCircle2 className="w-5 h-5 text-[var(--rs-coral)] mt-0.5 shrink-0" /> <span>{f}</span></li>
                    ))}
                  </ul>
                  <Link to={`/services/${s.id}`} data-testid={`services-row-link-${s.id}`} className="inline-flex items-center gap-1.5 mt-6 text-[var(--rs-coral)] font-medium hover:gap-2.5 transition-all">
                    Read more about {s.title} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default Services;
export { PageHeader };
